// New WhatsApp Settings (Phase 4) — separate route /platform/whatsapp.
// Re-exports the more detailed page I built above as a new component name.
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, Save, Plus, AlertTriangle, CheckCircle2 } from "lucide-react";

const Field = ({ label, value, onChange, type = "text", placeholder, dir }) => (
  <div>
    <label className="block text-[11px] text-stone-500 mb-1">{label}</label>
    <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder} dir={dir}
      className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#7A1F3D] outline-none text-sm" />
  </div>
);

export default function PlatformWhatsApp() {
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState({
    provider: "meta_cloud", phone_number_id: "", business_account_id: "",
    access_token: "", sender_display_name: "", enabled: false,
  });
  const [templates, setTemplates] = useState([]);
  const [newTpl, setNewTpl] = useState({ name: "", body_ar: "", body_en: "", category: "transactional" });
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, t, l] = await Promise.all([
        api.get("/notifications/whatsapp/status"),
        api.get("/notifications/whatsapp/templates"),
        api.get("/notifications/whatsapp/delivery-log?limit=20"),
      ]);
      setStatus(s.data);
      if (s.data.settings) setSettings((p) => ({ ...p, ...s.data.settings, access_token: "" }));
      setTemplates(t.data.items || []);
      setLog(l.data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل التحميل"); }
  };

  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setBusy(true);
    try { await api.put("/notifications/whatsapp/settings", settings); toast.success("تم الحفظ"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "فشل"); }
    finally { setBusy(false); }
  };

  const addTemplate = async () => {
    if (!newTpl.name || !newTpl.body_ar || !newTpl.body_en) { toast.error("املأ الحقول"); return; }
    setBusy(true);
    try {
      await api.post("/notifications/whatsapp/templates", newTpl);
      toast.success("تم إنشاء القالب");
      setNewTpl({ name: "", body_ar: "", body_en: "", category: "transactional" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل"); }
    finally { setBusy(false); }
  };

  const ready = status?.ready;
  const flagOn = status?.flag_enabled;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4" data-testid="platform-whatsapp-page">
      <div>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-stone-500">
          <MessageCircle size={14} className="text-[#7A1F3D]" /> Notifications · WhatsApp
        </div>
        <h1 className="text-2xl font-bold text-stone-800 mt-1">WhatsApp Settings (Platform)</h1>
      </div>

      <div className={`rounded-xl p-4 border flex items-start gap-3 ${ready ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="whatsapp-status-banner">
        {ready ? <CheckCircle2 className="text-emerald-600 mt-0.5" size={20} /> : <AlertTriangle className="text-amber-600 mt-0.5" size={20} />}
        <div className="text-sm">
          <div className="font-semibold text-stone-800">
            {ready ? "جاهز للإرسال" : flagOn ? "الـ flag مُفعّل لكن الإعدادات ناقصة" : "غير مفعّل"}
          </div>
          <div className="text-stone-600 text-xs mt-0.5">
            Flag: <strong>{flagOn ? "ON" : "OFF"}</strong> · Provider: <strong>{status?.settings?.provider || "—"}</strong> · Templates: <strong>{status?.templates_count ?? 0}</strong> · Delivered (24h): <strong>{status?.delivery_24h ?? 0}</strong>
          </div>
          {!flagOn && <div className="text-amber-700 text-[11px] mt-1">يحتاج تفعيل <code>notifications.whatsapp.enabled</code> من Tenancy Admin.</div>}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-stone-200 p-4">
        <h2 className="font-semibold text-stone-700 mb-3">إعدادات المزوّد</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] text-stone-500 mb-1">المزوّد</label>
            <select value={settings.provider} onChange={(e) => setSettings({ ...settings, provider: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm" data-testid="wa-provider">
              <option value="meta_cloud">Meta Cloud API</option>
              <option value="360dialog">360dialog</option>
              <option value="stub">Stub (testing)</option>
            </select>
          </div>
          <Field label="Sender display name" value={settings.sender_display_name} onChange={(v) => setSettings({ ...settings, sender_display_name: v })} dir="ltr" />
          <Field label="Phone number ID" value={settings.phone_number_id} onChange={(v) => setSettings({ ...settings, phone_number_id: v })} dir="ltr" />
          <Field label="Business account ID" value={settings.business_account_id} onChange={(v) => setSettings({ ...settings, business_account_id: v })} dir="ltr" />
          <Field label="Access token (سرّي)" value={settings.access_token} onChange={(v) => setSettings({ ...settings, access_token: v })} type="password" dir="ltr" placeholder="أدخل token جديد للتحديث" />
        </div>
        <div className="mt-3 flex justify-end">
          <button data-testid="wa-save-settings" disabled={busy} onClick={saveSettings} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#7A1F3D] to-[#9B2950] text-white text-sm">
            <Save size={14} /> حفظ
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-stone-200 p-4">
        <h2 className="font-semibold text-stone-700 mb-3">قوالب الرسائل</h2>
        <div className="grid sm:grid-cols-4 gap-2 mb-2">
          <Field label="اسم القالب" value={newTpl.name} onChange={(v) => setNewTpl({ ...newTpl, name: v })} dir="ltr" placeholder="booking_confirmed" />
          <div>
            <label className="block text-[11px] text-stone-500 mb-1">الفئة</label>
            <select value={newTpl.category} onChange={(e) => setNewTpl({ ...newTpl, category: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm">
              <option value="transactional">Transactional</option>
              <option value="marketing">Marketing</option>
              <option value="utility">Utility</option>
            </select>
          </div>
          <Field label="نص عربي" value={newTpl.body_ar} onChange={(v) => setNewTpl({ ...newTpl, body_ar: v })} placeholder="تأكيد حجزك يوم {{date}}" />
          <Field label="نص إنجليزي" value={newTpl.body_en} onChange={(v) => setNewTpl({ ...newTpl, body_en: v })} dir="ltr" placeholder="Booking confirmed for {{date}}" />
        </div>
        <button data-testid="wa-add-template" disabled={busy} onClick={addTemplate} className="text-sm text-[#7A1F3D] hover:underline inline-flex items-center gap-1.5">
          <Plus size={14} /> إضافة قالب
        </button>
        <div className="mt-3 divide-y divide-stone-100">
          {templates.map((t) => (
            <div key={t.template_id} className="py-2 text-sm" data-testid={`wa-tpl-${t.template_id}`}>
              <div className="flex items-center gap-2"><code className="font-mono text-xs">{t.name}</code><span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-600">{t.category}</span><span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">{t.status}</span></div>
              <div className="text-[11px] text-stone-600 mt-1 grid sm:grid-cols-2 gap-2">
                <div>AR: {t.body_ar}</div><div dir="ltr">EN: {t.body_en}</div>
              </div>
            </div>
          ))}
          {templates.length === 0 && <div className="text-sm text-stone-400 text-center py-3">لا توجد قوالب بعد.</div>}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-stone-200 p-4">
        <h2 className="font-semibold text-stone-700 mb-3">سجل الإرسال (آخر 20)</h2>
        {log.length === 0 ? (
          <div className="text-sm text-stone-400 text-center py-3">لم تُرسَل رسائل بعد.</div>
        ) : (
          <div className="text-[11px] space-y-1">
            {log.map((l, i) => (
              <div key={i} className="p-2 rounded bg-stone-50 flex items-center justify-between">
                <span><strong>{l.template_name}</strong> → {l.recipient}</span>
                <span className="text-stone-500">{l.status} · {l.ts?.slice(0, 19)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
